import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Sparkles, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/designer", label: "Design Studio" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-paper border-b-2 border-ink" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="grid place-items-center w-9 h-9 bg-brand-yellow neo-sm rounded-lg group-hover:rotate-6 transition-transform">
            <Sparkles className="w-5 h-5" />
          </span>
          <span className="font-display font-bold text-xl tracking-tight">FabricATE</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 font-body font-bold">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-brand-pink transition-colors" data-testid={`nav-${l.label.split(" ")[0].toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" className="hover:text-brand-pink transition-colors" data-testid="nav-admin">Admin</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/cart" className="relative grid place-items-center w-10 h-10 bg-white neo-sm rounded-lg neo-press" data-testid="nav-cart">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-brand-pink text-ink text-xs font-bold w-5 h-5 grid place-items-center rounded-full border-2 border-ink" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/account" className="grid place-items-center w-10 h-10 bg-brand-mint neo-sm rounded-lg neo-press" data-testid="nav-account">
                <User className="w-5 h-5" />
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="font-bold text-sm hover:text-brand-pink" data-testid="nav-logout">
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden sm:inline-flex px-4 py-2 bg-ink text-paper font-bold rounded-lg neo-press border-2 border-ink" data-testid="nav-login">
              Sign In
            </Link>
          )}

          <button className="md:hidden grid place-items-center w-10 h-10 bg-white neo-sm rounded-lg" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-ink bg-paper px-4 py-4 flex flex-col gap-3 font-bold" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}>{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/account" onClick={() => setOpen(false)}>My Account</Link>
              {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}>Admin</Link>}
              <button className="text-left" onClick={() => { logout(); setOpen(false); navigate("/"); }}>Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)}>Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
